import React from 'react';
import { Activity, AlertTriangle, Brain, TrendingUp } from 'lucide-react';

const toneClasses = {
  green: 'text-signal-green border-signal-green/30 bg-signal-green/5',
  blue: 'text-mentor-accent border-mentor-accent/30 bg-mentor-accent/5',
  yellow: 'text-signal-yellow border-signal-yellow/30 bg-signal-yellow/5',
  orange: 'text-signal-orange border-signal-orange/30 bg-signal-orange/5',
  red: 'text-signal-red border-signal-red/30 bg-signal-red/5',
};

function ProbabilityBar({ item }) {
  const cls = toneClasses[item.tone] || toneClasses.blue;
  return (
    <div className="rounded-xl border border-mentor-border/35 bg-mentor-bg/30 p-3">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div>
          <p className="font-display font-bold text-sm text-mentor-text">{item.label}</p>
          <p className="text-[10px] uppercase tracking-widest text-mentor-muted font-mono">{item.shortLabel}</p>
        </div>
        <span className={`px-2.5 py-1 rounded-full border text-xs font-mono font-bold ${cls}`}>{item.probability}%</span>
      </div>
      <div className="h-2 rounded-full bg-mentor-bg overflow-hidden border border-mentor-border/30">
        <div className={`h-full rounded-full ${item.tone === 'green' ? 'bg-signal-green' : item.tone === 'yellow' ? 'bg-signal-yellow' : item.tone === 'orange' ? 'bg-signal-orange' : item.tone === 'red' ? 'bg-signal-red' : 'bg-mentor-accent'}`} style={{ width: `${Math.max(3, Math.min(100, item.probability))}%` }} />
      </div>
    </div>
  );
}

function DriverPill({ driver }) {
  const points = Number(driver.points);
  const pointLabel = Number.isFinite(points)
    ? points > 0 ? `+${points}` : `${points}`
    : '—';
  return (
    <div className="rounded-xl border border-mentor-border/30 bg-mentor-bg/25 p-3">
      <div className="flex items-start justify-between gap-2 mb-1">
        <p className="text-[10px] uppercase tracking-widest text-mentor-muted font-display font-bold">{driver.label}</p>
        <span className="text-[10px] font-mono text-mentor-accent">{pointLabel}</span>
      </div>
      <p className="text-xs text-mentor-text/85 font-body leading-relaxed">{driver.detail}</p>
    </div>
  );
}

function ExplanationCard({ title, text, tone = 'blue' }) {
  const cls = tone === 'yellow'
    ? 'border-signal-yellow/25 bg-signal-yellow/5'
    : tone === 'green'
      ? 'border-signal-green/25 bg-signal-green/5'
      : 'border-mentor-border/35 bg-mentor-bg/25';
  return (
    <div className={`rounded-xl border p-3 ${cls}`}>
      <p className="text-[10px] uppercase tracking-widest text-mentor-muted font-display font-bold mb-1">{title}</p>
      <p className="text-xs text-mentor-text/90 font-body leading-relaxed">{text || '—'}</p>
    </div>
  );
}

export default function PredictiveRegimeProbability({ forecast, compact = false }) {
  if (!forecast?.enabled) return null;
  const mostLikelyTone = toneClasses[forecast?.mostLikely?.tone] || toneClasses.blue;
  const drivers = Array.isArray(forecast.drivers) ? forecast.drivers : [];
  const displayedDrivers = compact ? drivers.slice(0, 4) : drivers;

  return (
    <div className="glass-card p-6 animate-slide-up border border-mentor-accent/20" style={{ animationDelay: '0.42s' }}>
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Brain className="text-mentor-accent" size={20} />
            <h3 className="font-display font-bold text-lg text-mentor-text">Predictive Regime Probability Engine</h3>
          </div>
          <p className="text-sm text-mentor-muted font-body leading-relaxed max-w-4xl">
            {forecast.beginnerSummary}
          </p>
        </div>
        <div className={`rounded-2xl border px-4 py-3 min-w-[210px] ${mostLikelyTone}`}>
          <p className="text-[10px] uppercase tracking-widest font-display font-bold opacity-80">Most Likely Next</p>
          <p className="text-lg font-display font-bold leading-tight">{forecast?.mostLikely?.label || 'Building'}</p>
          <p className="text-xs font-mono opacity-90">{forecast?.mostLikely?.probability ?? '—'}% · {forecast.timeframe}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
        {(forecast.probabilities || []).map((item) => (
          <ProbabilityBar key={item.key} item={item} />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-5">
        <div className="rounded-xl border border-mentor-border/35 bg-mentor-bg/25 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="text-mentor-accent" size={16} />
            <p className="text-[10px] uppercase tracking-widest text-mentor-muted font-display font-bold">Risk Lean Score</p>
          </div>
          <p className="font-mono text-2xl text-mentor-text font-bold">{forecast.riskLeanScore}/100</p>
          <p className="text-xs text-mentor-muted font-body mt-1">Current: {forecast?.currentRegime?.label} at {forecast?.currentRegime?.score}/100</p>
        </div>
        <div className="rounded-xl border border-signal-yellow/25 bg-signal-yellow/5 p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="text-signal-yellow" size={16} />
            <p className="text-[10px] uppercase tracking-widest text-signal-yellow font-display font-bold">More Cautious Next Reading</p>
          </div>
          <p className="font-mono text-2xl text-signal-yellow font-bold">{forecast.cautionOrWorse}%</p>
          <p className="text-xs text-mentor-muted font-body mt-1">Chance the next clean snapshot may lean more cautious than All Clear.</p>
        </div>
        <div className="rounded-xl border border-mentor-border/35 bg-mentor-bg/25 p-4">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="text-mentor-accent" size={16} />
            <p className="text-[10px] uppercase tracking-widest text-mentor-muted font-display font-bold">Model Confidence</p>
          </div>
          <p className="font-display text-sm text-mentor-text font-bold">{forecast?.confidence?.label}</p>
          <p className="text-xs text-mentor-muted font-body mt-1">{forecast?.confidence?.note}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {displayedDrivers.map((driver) => (
          <DriverPill key={driver.key} driver={driver} />
        ))}
      </div>

      {!compact && forecast?.beginnerExplanation && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <ExplanationCard title="What this means" text={forecast.beginnerExplanation.whatItMeans} tone="green" />
          <ExplanationCard title="What to watch" text={forecast.beginnerExplanation.whatToWatch} tone="yellow" />
          <ExplanationCard title="What it does not mean" text={forecast.beginnerExplanation.whatItDoesNotMean} />
          <ExplanationCard
            title="Snapshot / Replay validation"
            text={forecast?.snapshotReplayValidation?.note || 'Clean daily snapshots can preserve this probability context for historical replay.'}
          />
        </div>
      )}

      <div className="mt-4 rounded-2xl border border-mentor-border/35 bg-mentor-bg/25 p-4">
        <p className="text-xs text-mentor-muted font-body leading-relaxed">
          <strong className="text-mentor-text">Important rule:</strong> {forecast.importantRule} This panel is a guide for attention, not a trading command.
        </p>
      </div>
    </div>
  );
}
